import React, { useState } from "react";
import { Button, Descriptions, Upload, Tooltip, Alert, Form } from "antd";
import "./upload-offline-dts.css";
import DeliverablesReview from "../UploadDTS/deliverables-review";
import ContactTable from "../Contact/ContactTable/contact-table";
import { DownloadOutlined, UploadOutlined, QuestionCircleOutlined, InfoCircleFilled, PaperClipOutlined } from "@ant-design/icons";
import { offlineUploadDTSText, formatToLongDate } from "../../../components/Shared/utils";
import { UploadChangeParam } from "antd/es/upload";
import { uploadAndProcessFile } from "../Details/data-format-file-upload";
import { SaveDraft } from "../../../services/save-draft";
import { useOpenNotification } from "../../../components/Notification/notification";
import ResultPage from "../../../components/GenericResult/result-page";
import { getUserEmail } from "../../../services/utils";
import { downloadFileUsingPreSignedURL } from "../../PackageHistory/utils";
import { downloadSignedDTS } from "../../../services/download-signed-dts";
import { useNavigate } from 'react-router-dom';
import { validateDataFormat } from "../Details/utils";

interface UploadOfflineDTSProps {
  rowDetails?: any;
  onUploadComplete?: (fileUrl: string) => void;
}

const UploadOfflineDTS: React.FC<UploadOfflineDTSProps> = ({
  rowDetails,
  onUploadComplete,
}) => {
  const [form] = Form.useForm();
  const { openNotification, contextHolder } = useOpenNotification();
  const [fileList, setFileList] = useState<any[]>([]);
  const [isSuccessPage, setIsSuccessPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const userEmail = getUserEmail();
  const navigate = useNavigate();

  const handleBack = () => {
      navigate('/dashboard');
  };

  const handleChange = async (info: UploadChangeParam) => {
    const { fileList: newFileList } = info;
    setFileList(newFileList);
    form.setFieldsValue({ signedDTSAgreement: newFileList });
    form.validateFields(["signedDTSAgreement"]);
  };

  const handleSubmit = async() => {
    const { errorFields } = await form.validateFields().catch((err) => err);
    if (errorFields?.length) return;
    setLoading(true);
    const isDTSUpload = await uploadAndProcessFile(
      fileList,
      rowDetails,
      Array.isArray(fileList) && fileList.every((file: any) => file.originFileObj),
      false,
      true,
    );
    if(isDTSUpload) {
      await SaveDraft({
        record_id: rowDetails?.record_id,
        status: "Upload offline DTS transfer",
        MSDCompound: rowDetails?.msd,
        msd_type: rowDetails?.msd_type,
        supplierCompound: rowDetails?.supplier_compound,
        supplierOrganization: rowDetails?.supplier_org,
        transferId: rowDetails?.transfer_id,
        requesters: rowDetails?.requesters,
        suppliers: rowDetails?.suppliers,
        updated_by: userEmail ?? undefined,
      })
        .then((response) => {
          setLoading(false);
          setIsSuccessPage(true);
          onUploadComplete?.("");
        }).catch((error: any) => {
          openNotification(
            "error",
            error?.message ? error?.message : "Failed to Submit Offline DTS Agreement.",
          );
          setLoading(false);
        });
    } else {
      openNotification("error", "Upload incomplete: no valid uploaded parts found. File may have been removed/quarantined locally. Please re-upload.");
      setLoading(false);
    };
  };

  const handleDTSDownloadClick = async () => {
    if (rowDetails) {
      try {
        const downloadUrl = await downloadSignedDTS(
          rowDetails.msd.split('-')[0],
          rowDetails.protocol_number,
          rowDetails.dts_version,
          rowDetails.transfer_id.split('-')[0],
          rowDetails.supplier_org,
          formatToLongDate(rowDetails.updated_when),
          false,
        );
        downloadFileUsingPreSignedURL(downloadUrl);
      } catch (error) {
        openNotification("error", "Failed to download DTS");
      }
    }
  };

  const trialDetailsFieldsValue = [
    {
      key: "1",
      label: "Source of MSD Compound and Protocol Number/Identifier",
      children: rowDetails?.msd_type ?? "",
    },
    {
      key: "2",
      label: "MSD Compound and Protocol Number/Identifier",
      children: rowDetails?.msd ?? "",
    },
    {
      key: "3",
      label: "Supplier",
      children: rowDetails?.supplier_org ?? "",
    },
    {
      key: "4",
      label: "Supplier Compound and Protocol Number/Identifier",
      children: rowDetails?.supplier_compound ?? "",
    },
  ];

  const transferInformationFieldsValue = [
    {
      key: "5",
      label: "Transfer ID",
      children: rowDetails?.transfer_id ?? "",
    },
    { key: "6", label: "DTS Version", children: rowDetails?.dts_version ?? "" },
    {
      key: "7",
      label: "Description of Change",
      children: rowDetails?.description_change ?? "",
    },
    {
      key: "8",
      label: "Purpose",
      children: rowDetails?.purpose ?? "",
    },
    {
      key: "9",
      label: "Data Format",
      children: rowDetails?.data_format?.length ? <><PaperClipOutlined /> {rowDetails?.data_format[0].name}</> : "",
    },
  ];

  return (
    <>
      {!isSuccessPage ? (
      <div>
      <Alert
        message={offlineUploadDTSText()}
        type="info"
        showIcon
        icon={<InfoCircleFilled style={{ color: "#008576" }} />}
        className="alert"
        style={{
          borderRadius: 0,
          borderColor: "#00855a24",
          background: "#00855a24",
          marginBottom: "10px",
        }}
      />
      <div className="card">
        <span className="titlePreview">Trial Details</span>
        <Descriptions
          column={1}
          size="small"
          bordered
          items={trialDetailsFieldsValue}
        />
      </div>
      <div className="card">
      <div className="titlePreview">Transfer Information</div>
      <Descriptions
        column={1}
        size="small"
        bordered
        items={transferInformationFieldsValue}
      />
      <div style={{ fontSize: "20px", marginBottom: 10, marginTop: 10 }}>Deliverables</div>
        <DeliverablesReview initialDeliverables={rowDetails?.init_deliverables} />
      </div>
      <div className="card">
          <div className="titlePreview">Contact Information</div>
          <div id="requester-signature" className="initiate">
            <ContactTable
              id="view-requester"
              title="Requester"
              contacts={rowDetails?.requesters}
              showActions={false}
            />
          </div>
          <div style={{ marginTop: '2rem' }}>
            <ContactTable
              id="view-supplier"
              title="Supplier"
              contacts={rowDetails?.suppliers}
              showActions={false}
            />
          </div>
      </div>
      <Form form={form} layout="vertical">
        <div className="card">
          <div className="titlePreview">Offline DTS Agreement</div>
          <div style={{ fontSize: "15px", marginBottom: 10, display: "flex", alignItems: "center", gap: "8px" }}>
            Offline DTS Agreement
            <Tooltip title="Download the offline DTS agreement generated for this transfer. One requester and one supplier must sign the agreement before it is re-uploaded.">
              <QuestionCircleOutlined style={{ color: "grey" }} />
            </Tooltip>
          </div>
          <Button icon={<DownloadOutlined />} style={{ marginBottom: "10px" }} onClick={handleDTSDownloadClick}>Download</Button>

          <Form.Item
            name="signedDTSAgreement"
            label="Signed DTS Agreement"
            required={true}
            tooltip="Upload the signed offline DTS agreement as a PDF."
            rules={[
              { required: true, message: "This is a required field" },
              { validator: validateDataFormat() },
            ]}
            style={{ marginTop: "10px" }}
          >
            <Upload
              fileList={fileList}
              onChange={handleChange}
              beforeUpload={() => false}
              accept=".pdf"
            >
              <Button icon={<UploadOutlined />}>Upload</Button>
            </Upload>
          </Form.Item>
        </div>
      </Form>
      <div className="card">
        <div style={{ display: "flex", gap: 16 }}>
          <Button type="primary" onClick={handleSubmit} loading={loading}>Submit</Button>
          <Button onClick={handleBack} disabled={loading}>Back</Button>
        </div>
      </div>
      </div>
      ) : (
        <ResultPage
          title="Offline DTS Agreement Submitted"
          subtitle="Your offline DTS agreement has been submitted. The supplier may now upload their data transfer."
        />
      )}
      {contextHolder}
    </>
  );
};

export default UploadOfflineDTS;
